// src/components/extensions/ExtensionCard.styles.ts
//
// One panel, used for every extension on the page. There is no second panel
// type and no per-extension branch in the markup — the optional parts below
// (licence, install line, demo, links) are driven purely by whether the config
// supplies them, so a new extension needs no new component.
//
// The visual vocabulary is still the one ProjectCard.styles.ts and
// SelectedWorkPreview.styles.ts already use: a hairline surface, a mono meta row
// above the title, hairline technology tags, and a transform-only hover that
// settles the border to the accent. What changed from the first pass of this
// page is the composition, not the design language — the panel is now full
// width with a leading icon tile, because this page showcases each extension on
// its own rather than setting two side by side.
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { minDevice } from '../../styles/breakpoints';

const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

/**
 * Shared reduced-motion guard, for the same reason Navbar.styles.ts keeps one:
 * these class rules outrank the global block in globalStyles.ts.
 */
const noMotion = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * Panel surface. Same hairline-on-background treatment as the project card, at
 * the full page measure. Transforms only on hover, so the lift cannot reflow
 * the list, and it is dropped under reduced motion.
 *
 * A motion.article: the inner pieces below are motion elements too, so the
 * cascade has to start somewhere framer-motion can propagate from.
 */
export const Panel = styled(motion.article)`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  width: 100%;
  min-width: 0;
  padding: 1.75rem;
  background: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.35s ease,
    box-shadow 0.35s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: ${({ theme }) => theme.accent};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  @media ${minDevice.tablet} {
    /* Room for the icon tile to sit beside the content rather than above it. */
    flex-direction: row;
    align-items: flex-start;
    gap: 1.75rem;
    padding: 2.5rem;
  }
`;

/* -------------------------------------------------------------- icon tile */

/**
 * The leading glyph. Carries the extension's identity, so it gets a real tile
 * rather than a bare icon floating in the text.
 *
 * The hover fill is the same accent the panel border goes to, which ties the
 * two states together. The glyph itself does not move on hover: it is not a
 * control, so a movement there would promise an action the tile does not have.
 */
export const IconTile = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.accent};
  transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;

  ${Panel}:hover & {
    background: ${({ theme }) => theme.cardBg};
    border-color: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.headingColor};
  }

  @media ${minDevice.tablet} {
    width: 64px;
    height: 64px;
  }

  ${noMotion}
`;

/** Holds the icon so framer-motion can size the glyph without a wrapper. */
export const IconGlyph = styled.span`
  display: inline-flex;
  line-height: 0;
`;

/* ------------------------------------------------------------- meta + head */

/** Everything to the right of the tile, or below it on a narrow screen. */
export const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  min-width: 0;
  flex: 1;
`;

/**
 * Position in the list, e.g. "01" of "02". Zero-padded so the numerals line up
 * as a column, which is the whole point of showing them. Decorative — the panel
 * heading carries the meaning, and the total would be noise read out.
 */
export const IndexNumeral = styled.span`
  font-family: ${monoStack};
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.accent};
  opacity: 0.7;
  font-variant-numeric: tabular-nums;
`;

export const MetaRow = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

export const PlatformText = styled.span`
  font-family: ${monoStack};
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.headingColor};
`;

export const MetaDivider = styled.span`
  color: ${({ theme }) => theme.borderColor};
`;

export const CategoryText = styled.span`
  font-family: ${monoStack};
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

/**
 * The trailing meta item — the licence. Reuses the featured-badge treatment from
 * the project card: hairline, small caps, pushed to the far end.
 */
export const MetaBadge = styled.span`
  margin-left: auto;
  padding: 0.2rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: ${monoStack};
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
  white-space: nowrap;
`;

export const CardTitle = styled(motion.h2)`
  margin: 0;
  font-size: 1.3rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
  font-weight: 700;
  color: ${({ theme }) => theme.headingColor};
  /* A long extension name must wrap rather than widen the panel. */
  overflow-wrap: break-word;

  @media ${minDevice.tablet} {
    font-size: 1.6rem;
  }
`;

export const CardDescription = styled(motion.p)`
  margin: 0;
  /* Wider than the project card's 62ch: on the full measure a longer line is
     easier to read, and this is the sentence the visitor came for. */
  max-width: 72ch;
  font-size: 0.975rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.text};
`;

/* --------------------------------------------------------------- features */

/**
 * Features as a real list, so a screen reader announces the count instead of one
 * undifferentiated run of text. The label is not visible: this is detail under
 * the description, and a visible heading would compete with the title.
 *
 * Two columns once there is room, since the panel now has the width for it.
 */
export const FeatureList = styled(motion.ul)`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.45rem;

  @media ${minDevice.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 1.75rem;
  }
`;

export const FeatureItem = styled.li`
  position: relative;
  padding-left: 1rem;
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.secondary};
  overflow-wrap: break-word;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.65em;
    width: 4px;
    height: 4px;
    border-radius: ${({ theme }) => theme.borderRadius.full};
    background: ${({ theme }) => theme.accent};
  }
`;

/* ------------------------------------------------------------------ media */

/**
 * Slot for an optional inline demo. Exists mostly to carry the reveal variant
 * into the list cascade: the demo's own root is a plain styled div, so without
 * this it would appear unanimated while everything around it faded in.
 *
 * min-width: 0 because the demo scrolls its own code horizontally, and a flex
 * child will not shrink below its content width without it.
 */
export const DemoSlot = styled(motion.div)`
  width: 100%;
  min-width: 0;
`;

/**
 * The optional demo preview.
 *
 * The aspect-ratio box is load-bearing: without it the panel reflows when the
 * image lands and the whole column shifts. Lazy and async-decoded so a demo
 * recording never blocks the page. `object-fit: cover` matches the existing
 * visual treatment in SelectedWorkPreview.styles.ts.
 */
export const DemoFigure = styled(motion.figure)`
  margin: 0;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

/* ----------------------------------------------------------------- footer */

/**
 * The bottom block, pushed to the end of the content column with `margin-top:
 * auto` so the tag and link rows sit on the panel's lower edge however long the
 * feature list above it runs.
 */
export const CardFooter = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  margin-top: auto;
  padding-top: 0.35rem;
`;

/**
 * The install command, set as mono text behind a hairline label. Presentational
 * only — the command is documentation, not an action, so it is a <code> and not
 * a button.
 */
export const InstallLine = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
  margin: 0;
  min-width: 0;
`;

export const InstallLabel = styled.span`
  font-family: ${monoStack};
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const InstallCommand = styled.code`
  font-family: ${monoStack};
  font-size: 0.75rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.secondary};
  /* A long command must break inside its own box rather than push the panel
     wider than the page. */
  overflow-wrap: break-word;
`;

/* Hairline tags rather than filled chips, matching TechList / TechTag. */

export const TechList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

export const TechTag = styled.li`
  padding: 0.2rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: ${monoStack};
  font-size: 0.7rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.secondary};
  overflow-wrap: break-word;
`;

/**
 * Outbound links. Hairline like the tech tags, sized up to a usable target:
 * these are the panel's only actions, and a 0.7rem tag is not a thing to aim a
 * finger at on a phone.
 */
export const LinkList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const LinkItem = styled.li`
  min-width: 0;
`;

export const LinkAnchor = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.7rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: ${monoStack};
  font-size: 0.72rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.secondary};
  text-decoration: none;
  transition: color 0.2s ease, border-color 0.2s ease;

  /* globalStyles.ts underlines every a[href]:hover at (0,2,1). A button that
     underlines on hover reads as a broken link, so it is cancelled at (0,3,0). */
  &&:hover {
    color: ${({ theme }) => theme.text};
    border-color: ${({ theme }) => theme.accent};
    text-decoration: none;
  }

  /* Same specificity problem as the underline: the global fixed-grey ring at
     (0,2,1) would replace the themed one, so the ring is doubled to (0,3,0). */
  &&:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.background},
      0 0 0 4px ${({ theme }) => theme.accent};
  }

  svg {
    flex-shrink: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: color 0.2s ease, border-color 0.2s ease;
  }
`;

/**
 * Announces that a link opens in a new tab. Without it a screen reader user gets
 * no warning before the context changes, since the visual arrow icon is
 * aria-hidden.
 */
export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
